import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranch } from '../BranchContext';
import { getSupportedCurrencies } from '../productsApi';
import { fetchCategories } from '../categoriesApi';
import {
  fetchRentalProducts, fetchAllRentals, fetchRentalStats,
  createRentalProduct, updateRentalProduct, deleteRentalProduct,
  fetchRentalProductById
} from '../api/rentalApi';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardMedia, CardActions,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Alert, Snackbar, CircularProgress, Divider, Stack, Avatar, Tooltip,
  alpha, useTheme, useMediaQuery, InputAdornment, Checkbox, CardActionArea, Portal
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';

// ══════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════

const STATUS_CONFIG = {
  Pending: { color: '#FF9800', icon: <ScheduleIcon />, labelAr: 'قيد الانتظار', labelEn: 'Pending' },
  Confirmed: { color: '#2196F3', icon: <CheckCircleIcon />, labelAr: 'مؤكد', labelEn: 'Confirmed' },
  Active: { color: '#4CAF50', icon: <HomeRepairServiceIcon />, labelAr: 'نشط', labelEn: 'Active' },
  Returned: { color: '#9E9E9E', icon: <AssignmentReturnIcon />, labelAr: 'تم الإرجاع', labelEn: 'Returned' },
  Overdue: { color: '#F44336', icon: <WarningIcon />, labelAr: 'متأخر', labelEn: 'Overdue' },
  Cancelled: { color: '#757575', icon: <CancelIcon />, labelAr: 'ملغي', labelEn: 'Cancelled' },
};

const PRICE_TYPE_LABELS = {
  hourly: { en: 'Per Hour', ar: 'بالساعة' },
  daily: { en: 'Per Day', ar: 'باليوم' },
  weekly: { en: 'Per Week', ar: 'بالأسبوع' },
  monthly: { en: 'Per Month', ar: 'بالشهر' },
};

const getImageUrl = (imageData) => {
  if (!imageData) return null;
  if (typeof imageData === 'string') {
    try { const p = JSON.parse(imageData); return p.medium || p.large || p.thumbnail || imageData; }
    catch { return imageData; }
  }
  if (typeof imageData === 'object') return imageData.medium || imageData.thumbnail || imageData.large;
  return null;
};

// Helper to extract customer info from various API response formats
const getCustomerName = (order) => {
  return order.CustomerName || order.customerName
    || order.Customer?.Name || order.Customer?.name
    || order.Customer?.FullName || order.Customer?.fullName
    || order.CustomerFullName || order.customerFullName
    || order.User?.Name || order.User?.name
    || order.User?.FullName
    || null;
};

const getCustomerPhone = (order) => {
  return order.CustomerPhone || order.customerPhone
    || order.Customer?.Phone || order.Customer?.phone
    || order.Customer?.PhoneNumber || order.Customer?.phoneNumber
    || order.CustomerPhoneNumber || order.customerPhoneNumber
    || order.User?.Phone || order.User?.phone
    || order.User?.PhoneNumber
    || null;
};

const getCustomerEmail = (order) => {
  return order.CustomerEmail || order.customerEmail
    || order.Customer?.Email || order.Customer?.email
    || order.User?.Email || order.User?.email
    || null;
};

// ══════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════

export default function RentalManagement() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedBranch } = useBranch();
  const isAr = i18n.language === 'ar';

  const branchId = selectedBranch?.id || selectedBranch?.Id;

  // ── Tab & Loading ──
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Products ──
  const [rentalProducts, setRentalProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);

  // ── Product Form ──
  const [formStep, setFormStep] = useState(0);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const emptyForm = {
    ProductName: '', Description: '', CategoryId: '', IsActive: true, ImageJson: '',
    CurrencyPrices: { YER: '' }, RentalPriceType: 'daily', RentalCurrencyPrices: { YER: '' },
    RentalMinDuration: 1, RentalMaxDuration: '', RentalDepositCurrencyPrices: { YER: '' },
    RentalStock: '', RentalTerms: '', RentalAvailableFrom: '', RentalAvailableTo: '',
  };
  const [productForm, setProductForm] = useState(emptyForm);

  // ── Orders (read-only) ──
  const [rentalOrders, setRentalOrders] = useState([]);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersPerPage, setOrdersPerPage] = useState(25);
  const [ordersTotalCount, setOrdersTotalCount] = useState(0);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('');
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // ── Stats ──
  const [stats, setStats] = useState(null);

  // ══════════════════════════════════════════
  // DATA LOADING
  // ══════════════════════════════════════════

  const loadProducts = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const data = await fetchRentalProducts(branchId);
      setRentalProducts(data || []);
    } catch (err) {
      console.error('Error loading rental products:', err);
      setSnackbar({ open: true, message: isAr ? 'فشل تحميل المنتجات' : 'Failed to load products', severity: 'error' });
    } finally { setLoading(false); }
  }, [branchId, isAr]);

  const loadOrders = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const data = await fetchAllRentals(branchId, ordersStatusFilter, ordersPage + 1, ordersPerPage);
      console.log('📦 Rental orders response:', JSON.stringify(data?.data?.[0], null, 2));
      setRentalOrders(data.data || []);
      setOrdersTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading rental orders:', err);
      setSnackbar({ open: true, message: isAr ? 'فشل تحميل الطلبات' : 'Failed to load orders', severity: 'error' });
    } finally { setLoading(false); }
  }, [branchId, ordersStatusFilter, ordersPage, ordersPerPage, isAr]);

  const loadStats = useCallback(async () => {
    if (!branchId) return;
    try {
      const data = await fetchRentalStats(branchId);
      setStats(data.Stats || null);
    } catch (err) { console.error('Error loading stats:', err); }
  }, [branchId]);

  const loadInitialData = useCallback(async () => {
    if (!branchId) return;
    try {
      const [cats, currs] = await Promise.all([fetchCategories(branchId), getSupportedCurrencies()]);
      const filtered = (cats || []).filter(c => {
        const name = c.categoryName || c.CategoryName || '';
        return !name.startsWith("العروض:") && !name.startsWith("مجموعة:") && name !== "Offer" && name !== "Deal";
      });
      setCategories(filtered);
      setCurrencies(currs?.Currencies || [
          { code: 'YER', symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0 },
          { code: 'YRN', symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0 },
          { code: 'USD', symbol: '$', name: 'دولار أمريكي', decimals: 2 },
          { code: 'SAR', symbol: 'ر.س', name: 'ريال سعودي', decimals: 2 }
      ]);
    } catch (err) { console.error('Error loading initial data:', err); }
  }, [branchId]);

  useEffect(() => { loadInitialData(); loadStats(); }, [loadInitialData, loadStats]);
  useEffect(() => {
    if (tabValue === 0 && formStep === 0) loadProducts();
    if (tabValue === 1) loadOrders();
  }, [tabValue, formStep, loadProducts, loadOrders]);

  // ══════════════════════════════════════════
  // CURRENCY HELPERS
  // ══════════════════════════════════════════

  const initPrices = () => { const p = {}; currencies.forEach(c => { p[c.code] = ''; }); return p; };
  const fillPrices = (prices) => { const r = initPrices(); if (prices && typeof prices === 'object') Object.keys(prices).forEach(k => { r[k] = prices[k]; }); return r; };
  const cleanPrices = (prices) => { const c = {}; Object.keys(prices).forEach(k => { const v = parseFloat(prices[k]); if (!isNaN(v) && v > 0) c[k] = v; }); return Object.keys(c).length > 0 ? c : null; };
  const handlePriceChange = (field, currency, value) => { setProductForm(p => ({ ...p, [field]: { ...p[field], [currency]: value } })); };

  // ══════════════════════════════════════════
  // PRODUCT FORM HANDLERS
  // ══════════════════════════════════════════

  const handleOpenAddProduct = () => {
    setEditingProduct(null);
    setProductForm({ ...emptyForm, CurrencyPrices: initPrices(), RentalCurrencyPrices: initPrices(), RentalDepositCurrencyPrices: initPrices() });
    setImageFile(null); setImagePreview(null); setSelectedCategoryName(''); setFormStep(1);
  };

  const handleCategorySelect = (category) => {
    setProductForm(p => ({ ...p, CategoryId: category.id || category.Id }));
    setSelectedCategoryName(category.categoryName || category.CategoryName); setFormStep(2);
  };

  const handleOpenEditProduct = async (product) => {
    try {
      setLoading(true);
      const full = await fetchRentalProductById(product.Id);
      setEditingProduct(full);
      setProductForm({
        ProductName: full.ProductName || '', Description: full.Description || '', CategoryId: full.CategoryId || '',
        IsActive: full.IsActive !== false, ImageJson: '', CurrencyPrices: fillPrices(full.CurrencyPrices),
        RentalPriceType: full.RentalPriceType || 'daily', RentalCurrencyPrices: fillPrices(full.RentalCurrencyPrices),
        RentalMinDuration: full.RentalMinDuration || 1, RentalMaxDuration: full.RentalMaxDuration || '',
        RentalDepositCurrencyPrices: fillPrices(full.RentalDepositCurrencyPrices), RentalStock: full.RentalStock || '',
        RentalTerms: full.RentalTerms || '',
        RentalAvailableFrom: full.RentalAvailableFrom ? full.RentalAvailableFrom.split('T')[0] : '',
        RentalAvailableTo: full.RentalAvailableTo ? full.RentalAvailableTo.split('T')[0] : '',
      });
      setImagePreview(getImageUrl(full.ImageUrl)); setImageFile(null);
      const cat = categories.find(c => (c.id || c.Id) === full.CategoryId);
      setSelectedCategoryName(cat ? (cat.categoryName || cat.CategoryName) : ''); setFormStep(2);
    } catch (err) {
      console.error('Error loading product:', err);
      setSnackbar({ open: true, message: isAr ? 'فشل تحميل المنتج' : 'Failed to load product', severity: 'error' });
    } finally { setLoading(false); }
  };

  const handleImageChange = (e) => { const f = e.target.files?.[0]; if (!f) return; setImageFile(f); setImagePreview(URL.createObjectURL(f)); };

  const handleSaveProduct = async (e) => {
    e?.preventDefault();
    if (!branchId) { setSnackbar({ open: true, message: isAr ? 'يرجى اختيار المتجرأولاً' : 'Please select a branch first', severity: 'warning' }); return; }
    if (!productForm.ProductName.trim()) { setSnackbar({ open: true, message: isAr ? 'اسم المنتج مطلوب' : 'Product name is required', severity: 'warning' }); return; }
    const currencyPrices = cleanPrices(productForm.CurrencyPrices);
    if (!currencyPrices) { setSnackbar({ open: true, message: isAr ? 'يجب إدخال سعر واحد على الأقل' : 'At least one price is required', severity: 'warning' }); return; }
    const rentalCurrencyPrices = cleanPrices(productForm.RentalCurrencyPrices);
    if (!rentalCurrencyPrices) { setSnackbar({ open: true, message: isAr ? 'يجب إدخال سعر الإيجار' : 'Rental price is required', severity: 'warning' }); return; }

    setLoading(true);
    try {
      let imageJson = null;
      if (imageFile) { const reader = new FileReader(); imageJson = await new Promise((resolve) => { reader.onload = (ev) => resolve(ev.target.result); reader.readAsDataURL(imageFile); }); }
      const payload = {
        ProductName: productForm.ProductName, Description: productForm.Description,
        CategoryId: productForm.CategoryId ? parseInt(productForm.CategoryId) : null,
        BranchId: branchId, IsActive: productForm.IsActive, CurrencyPrices: currencyPrices, IsRental: true,
        RentalPriceType: productForm.RentalPriceType, RentalCurrencyPrices: rentalCurrencyPrices,
        RentalMinDuration: parseInt(productForm.RentalMinDuration) || 1,
        RentalMaxDuration: productForm.RentalMaxDuration ? parseInt(productForm.RentalMaxDuration) : null,
        RentalDepositCurrencyPrices: cleanPrices(productForm.RentalDepositCurrencyPrices),
        RentalStock: productForm.RentalStock ? parseInt(productForm.RentalStock) : null,
        RentalTerms: productForm.RentalTerms || null,
        RentalAvailableFrom: productForm.RentalAvailableFrom || null,
        RentalAvailableTo: productForm.RentalAvailableTo || null,
      };
      if (imageJson) payload.ImageJson = imageJson;
      if (editingProduct) {
        payload.Id = editingProduct.Id;
        await updateRentalProduct(editingProduct.Id, payload);
        setSnackbar({ open: true, message: isAr ? 'تم تحديث المنتج بنجاح' : 'Product updated', severity: 'success' });
      } else {
        if (!imageJson) { setSnackbar({ open: true, message: isAr ? 'صورة المنتج مطلوبة' : 'Product image is required', severity: 'warning' }); setLoading(false); return; }
        await createRentalProduct(payload);
        setSnackbar({ open: true, message: isAr ? 'تم إضافة المنتج بنجاح' : 'Product created', severity: 'success' });
      }
      setFormStep(0); loadProducts(); loadStats();
    } catch (err) {
      console.error('Error saving product:', err);
      setSnackbar({ open: true, message: err.response?.data?.ErrorMessage || (isAr ? 'فشل حفظ المنتج' : 'Failed to save'), severity: 'error' });
    } finally { setLoading(false); }
  };

  const handleDeleteProduct = async () => {
    if (!deletingProduct) return;
    setLoading(true);
    try {
      await deleteRentalProduct(deletingProduct.Id);
      setSnackbar({ open: true, message: isAr ? 'تم حذف المنتج' : 'Product deleted', severity: 'success' });
      setDeleteDialogOpen(false); setDeletingProduct(null); loadProducts(); loadStats();
    } catch (err) {
      setSnackbar({ open: true, message: isAr ? 'فشل حذف المنتج' : 'Failed to delete', severity: 'error' });
    } finally { setLoading(false); }
  };

  const filteredProducts = rentalProducts.filter(p =>
    p.ProductName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.Description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ══════════════════════════════════════════
  // RENDER: STATS
  // ══════════════════════════════════════════

  const renderStats = () => {
    if (!stats) return null;
    const cards = [
      { label: isAr ? 'منتجات التأجير' : 'Rental Products', value: stats.TotalRentalProducts, color: '#1976d2', icon: <InventoryIcon /> },
      { label: isAr ? 'إجمالي الطلبات' : 'Total Orders', value: stats.TotalRentals, color: '#9c27b0', icon: <AssignmentIcon /> },
      { label: isAr ? 'قيد الانتظار' : 'Pending', value: stats.Pending, color: '#FF9800', icon: <ScheduleIcon /> },
      { label: isAr ? 'نشط' : 'Active', value: stats.Active, color: '#4CAF50', icon: <HomeRepairServiceIcon /> },
      { label: isAr ? 'متأخر' : 'Overdue', value: stats.ActuallyOverdue, color: '#F44336', icon: <WarningIcon /> },
      { label: isAr ? 'تم الإرجاع' : 'Returned', value: stats.Returned, color: '#9E9E9E', icon: <AssignmentReturnIcon /> },
    ];
    return (
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
        {cards.map((s, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', borderTop: `3px solid ${s.color}`, borderRadius: 2 }}>
              <Avatar sx={{ bgcolor: alpha(s.color, 0.1), color: s.color, mx: 'auto', mb: 1, width: { xs: 34, sm: 40 }, height: { xs: 34, sm: 40 } }}>{s.icon}</Avatar>
              <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>{s.value || 0}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{s.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  // ══════════════════════════════════════════
  // RENDER: CATEGORY SELECTION
  // ══════════════════════════════════════════

  const renderCategorySelection = () => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={{ xs: 2, sm: 3 }}>
        <IconButton onClick={() => setFormStep(0)} size={isMobile ? "small" : "medium"}><ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /></IconButton>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{isAr ? 'اختر التصنيف' : 'Select Category'}</Typography>
      </Stack>
      <Alert severity="info" sx={{ mb: 3 }}>{isAr ? 'اختر تصنيفاً لمنتج التأجير' : 'Select a category for the rental product'}</Alert>
      {categories.length === 0 ? (
        <Alert severity="warning">{isAr ? 'لا توجد تصنيفات' : 'No categories found'}</Alert>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {categories.map((cat) => (
            <Grid item xs={6} sm={4} md={3} key={cat.id || cat.Id}>
              <Card sx={{ cursor: 'pointer', transition: '0.3s', borderRadius: 2, '&:hover': { boxShadow: 6 } }} onClick={() => handleCategorySelect(cat)}>
                <CardActionArea sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', minHeight: { xs: 100, sm: 120 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <CategoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>{cat.categoryName || cat.CategoryName}</Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  // ══════════════════════════════════════════
  // RENDER: PRODUCT FORM
  // ══════════════════════════════════════════

  const renderProductForm = () => (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={{ xs: 2, sm: 3 }}>
        <IconButton onClick={() => editingProduct ? setFormStep(0) : setFormStep(1)} size={isMobile ? "small" : "medium"}><ArrowBackIcon /></IconButton>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {editingProduct ? (isAr ? 'تعديل منتج التأجير' : 'Edit Rental Product') : (isAr ? 'إضافة منتج تأجير' : 'Add Rental Product')}
        </Typography>
        {selectedCategoryName && <Chip label={selectedCategoryName} color="primary" size="small" icon={<CategoryIcon sx={{ fontSize: 14 }} />} onDelete={editingProduct ? undefined : () => setFormStep(1)} sx={{ ml: 'auto' }} />}
      </Stack>
      <form onSubmit={handleSaveProduct}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}><Typography variant="subtitle2" fontWeight={700} color="primary">{isAr ? 'المعلومات الأساسية' : 'Basic Information'}</Typography></Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">{isAr ? 'اسم المنتج' : 'Product Name'} *</Typography>
            <TextField fullWidth value={productForm.ProductName} onChange={e => setProductForm(p => ({ ...p, ProductName: e.target.value }))} required size={isMobile ? "small" : "medium"} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">{isAr ? 'الحالة' : 'Status'}</Typography>
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
              <FormControlLabel control={<Checkbox checked={productForm.IsActive} onChange={e => setProductForm(p => ({ ...p, IsActive: e.target.checked }))} size="small" />}
                label={isAr ? 'المنتج نشط' : 'Product is Active'} />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={isMobile ? 2 : 3} label={isAr ? 'الوصف' : 'Description'} value={productForm.Description} onChange={e => setProductForm(p => ({ ...p, Description: e.target.value }))} size={isMobile ? "small" : "medium"} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">{isAr ? 'صورة المنتج' : 'Product Image'} {!editingProduct && '*'}</Typography>
            <Box sx={{ border: '2px dashed', borderColor: imagePreview ? 'success.main' : 'divider', p: 2, borderRadius: 2, textAlign: 'center', bgcolor: imagePreview ? alpha(theme.palette.success.main, 0.05) : '#fafafa' }}>
              <input accept="image/*" style={{ display: 'none' }} id="rental-image-upload" type="file" onChange={handleImageChange} />
              <label htmlFor="rental-image-upload">
                <Button variant="contained" component="span" startIcon={<PhotoCameraIcon />} color={imagePreview ? 'success' : 'primary'} size={isMobile ? "medium" : "large"} fullWidth={isMobile}>
                  {imagePreview ? (isAr ? '✓ تم اختيار الصورة' : '✓ Image Selected') : (isAr ? 'رفع صورة' : 'Upload Image')}
                </Button>
              </label>
              {imagePreview && <Card sx={{ width: { xs: '100%', sm: 200 }, maxWidth: 300, height: 200, boxShadow: 3, mx: 'auto', mt: 2 }}><CardMedia component="img" image={imagePreview} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Card>}
            </Box>
          </Grid>
          <Grid item xs={12}><Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" fontWeight={700} color="primary">{isAr ? 'سعر البيع (مرجعي)' : 'Sale Price (Reference)'} *</Typography>
          </Grid>
          {currencies.map(cur => (
            <Grid item xs={12} sm={4} key={`price-${cur.code}`}>
              <Paper elevation={1} sx={{ p: 1.5, bgcolor: '#f9f9f9', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                <Chip label={`${cur.symbol} ${cur.code}`} color="primary" size="small" sx={{ mb: 1 }} />
                <TextField fullWidth size="small" type="number" value={productForm.CurrencyPrices[cur.code] || ''}
                  onChange={e => handlePriceChange('CurrencyPrices', cur.code, e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">{cur.symbol}</InputAdornment> }}
                  inputProps={{ step: cur.decimals === 0 ? "1" : "0.01", min: "0" }} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{cur.name}</Typography>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="subtitle2" fontWeight={700} color="primary">🏠 {isAr ? 'إعدادات التأجير' : 'Rental Settings'}</Typography></Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{isAr ? 'نوع السعر' : 'Price Type'}</InputLabel>
              <Select value={productForm.RentalPriceType} onChange={e => setProductForm(p => ({ ...p, RentalPriceType: e.target.value }))} label={isAr ? 'نوع السعر' : 'Price Type'}>
                {Object.keys(PRICE_TYPE_LABELS).map(k => <MenuItem key={k} value={k}>{isAr ? PRICE_TYPE_LABELS[k].ar : PRICE_TYPE_LABELS[k].en}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label={isAr ? 'الحد الأدنى' : 'Min Duration'} type="number" value={productForm.RentalMinDuration} onChange={e => setProductForm(p => ({ ...p, RentalMinDuration: e.target.value }))} inputProps={{ min: 1 }} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label={isAr ? 'الحد الأقصى' : 'Max Duration'} type="number" value={productForm.RentalMaxDuration} onChange={e => setProductForm(p => ({ ...p, RentalMaxDuration: e.target.value }))} placeholder={isAr ? 'غير محدود' : 'Unlimited'} />
          </Grid>
          <Grid item xs={12}><Typography variant="caption" fontWeight={600} color="text.secondary">{isAr ? 'أسعار الإيجار *' : 'Rental Prices *'}</Typography></Grid>
          {currencies.map(cur => (
            <Grid item xs={12} sm={4} key={`rental-${cur.code}`}>
              <Paper elevation={1} sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.03), borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Chip label={`${cur.symbol} ${cur.code}`} color="info" size="small" sx={{ mb: 1 }} />
                <TextField fullWidth size="small" type="number" value={productForm.RentalCurrencyPrices[cur.code] || ''}
                  onChange={e => handlePriceChange('RentalCurrencyPrices', cur.code, e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">{cur.symbol}</InputAdornment> }}
                  inputProps={{ step: cur.decimals === 0 ? "1" : "0.01", min: "0" }} />
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12}><Typography variant="caption" fontWeight={600} color="text.secondary">{isAr ? 'مبلغ التأمين (اختياري)' : 'Security Deposit (optional)'}</Typography></Grid>
          {currencies.map(cur => (
            <Grid item xs={12} sm={4} key={`deposit-${cur.code}`}>
              <TextField fullWidth size="small" type="number" label={`${isAr ? 'التأمين' : 'Deposit'} (${cur.symbol})`}
                value={productForm.RentalDepositCurrencyPrices[cur.code] || ''}
                onChange={e => handlePriceChange('RentalDepositCurrencyPrices', cur.code, e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">{cur.symbol}</InputAdornment> }} inputProps={{ min: "0" }} />
            </Grid>
          ))}
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label={isAr ? 'المخزون' : 'Stock'} type="number" value={productForm.RentalStock} onChange={e => setProductForm(p => ({ ...p, RentalStock: e.target.value }))} placeholder={isAr ? 'غير محدود' : 'Unlimited'} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label={isAr ? 'متاح من' : 'Available From'} type="date" value={productForm.RentalAvailableFrom} onChange={e => setProductForm(p => ({ ...p, RentalAvailableFrom: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label={isAr ? 'متاح حتى' : 'Available To'} type="date" value={productForm.RentalAvailableTo} onChange={e => setProductForm(p => ({ ...p, RentalAvailableTo: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label={isAr ? 'شروط وأحكام التأجير' : 'Rental Terms & Conditions'} value={productForm.RentalTerms} onChange={e => setProductForm(p => ({ ...p, RentalTerms: e.target.value }))} size={isMobile ? "small" : "medium"} />
          </Grid>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
              <Button type="submit" variant="contained" color="success" size={isMobile ? "medium" : "large"} disabled={loading} fullWidth={isMobile}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} sx={{ px: 4, py: 1.2, fontWeight: 'bold', boxShadow: 3 }}>
                {loading ? (isAr ? 'جاري الحفظ...' : 'Saving...') : editingProduct ? (isAr ? 'تحديث المنتج' : 'Update Product') : (isAr ? 'إنشاء المنتج' : 'Create Product')}
              </Button>
              <Button variant="outlined" color="error" size={isMobile ? "medium" : "large"} fullWidth={isMobile} onClick={() => setFormStep(0)} sx={{ px: 4 }}>
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );

  // ══════════════════════════════════════════
  // RENDER: PRODUCTS LIST
  // ══════════════════════════════════════════

  const renderProductsList = () => (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
        <TextField size="small" placeholder={isAr ? 'بحث...' : 'Search...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ width: { xs: '100%', sm: 300 } }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddProduct} fullWidth={isMobile}>{isAr ? 'إضافة منتج تأجير' : 'Add Rental Product'}</Button>
      </Stack>
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
      ) : filteredProducts.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <HomeRepairServiceIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">{isAr ? 'لا توجد منتجات تأجير' : 'No rental products'}</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAddProduct} sx={{ mt: 2 }}>{isAr ? 'أضف أول منتج' : 'Add first product'}</Button>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {filteredProducts.map(product => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.Id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, transition: '0.2s', '&:hover': { boxShadow: 6 } }}>
                <CardMedia component="img" height={isMobile ? 150 : 180} image={getImageUrl(product.ImageUrl) || '/placeholder.png'} alt={product.ProductName} sx={{ objectFit: 'cover' }} />
                <CardContent sx={{ flex: 1, pb: 1, px: { xs: 1.5, sm: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="start">
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, mr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.ProductName}</Typography>
                    <Chip label={product.IsActive ? (isAr ? 'نشط' : 'Active') : (isAr ? 'معطل' : 'Inactive')} size="small" color={product.IsActive ? 'success' : 'default'} sx={{ fontSize: '0.65rem' }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.Description || '-'}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">{isAr ? 'سعر الإيجار:' : 'Rental:'}</Typography>
                      <Typography variant="body2" fontWeight={600} color="primary.main">{product.RentalFormattedPrice || '-'}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">{isAr ? 'النوع:' : 'Type:'}</Typography>
                      <Chip label={isAr ? PRICE_TYPE_LABELS[product.RentalPriceType]?.ar : PRICE_TYPE_LABELS[product.RentalPriceType]?.en} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    </Stack>
                    {product.RentalStock != null && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">{isAr ? 'المخزون:' : 'Stock:'}</Typography>
                        <Typography variant="body2" fontWeight={500}>{product.RentalStock}</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">{isAr ? 'المدة:' : 'Duration:'}</Typography>
                      <Typography variant="body2">{product.RentalMinDuration}{product.RentalMaxDuration ? ` - ${product.RentalMaxDuration}` : '+'}</Typography>
                    </Stack>
                  </Stack>
                </CardContent>
                <CardActions sx={{ pt: 0, px: { xs: 1, sm: 2 }, pb: 1.5 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenEditProduct(product)}>{isAr ? 'تعديل' : 'Edit'}</Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => { setDeletingProduct(product); setDeleteDialogOpen(true); }}>{isAr ? 'حذف' : 'Delete'}</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  // ══════════════════════════════════════════
  // RENDER: ORDERS TAB (READ-ONLY)
  // ══════════════════════════════════════════

  const renderOrdersTab = () => (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
          <InputLabel>{isAr ? 'الحالة' : 'Status'}</InputLabel>
          <Select value={ordersStatusFilter} onChange={(e) => { setOrdersStatusFilter(e.target.value); setOrdersPage(0); }} label={isAr ? 'الحالة' : 'Status'}>
            <MenuItem value="">{isAr ? 'الكل' : 'All'}</MenuItem>
            {Object.keys(STATUS_CONFIG).map(s => (
              <MenuItem key={s} value={s}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_CONFIG[s].color }} />
                  <span>{isAr ? STATUS_CONFIG[s].labelAr : s}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton onClick={loadOrders}><RefreshIcon /></IconButton>
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{isAr ? 'رقم' : '#'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{isAr ? 'المنتج' : 'Product'}</TableCell>
                  {!isMobile && <TableCell sx={{ fontWeight: 700 }}>{isAr ? 'العميل' : 'Customer'}</TableCell>}
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{isAr ? 'الفترة' : 'Period'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{isAr ? 'المبلغ' : 'Amount'}</TableCell>
                  {!isMobile && <TableCell sx={{ fontWeight: 700 }}>{isAr ? 'التوصيل' : 'Delivery'}</TableCell>}
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{isAr ? 'الحالة' : 'Status'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{isAr ? 'عرض' : 'View'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rentalOrders.length === 0 ? (
                  <TableRow><TableCell colSpan={isMobile ? 6 : 8} align="center" sx={{ py: 4 }}><Typography color="text.secondary">{isAr ? 'لا توجد طلبات' : 'No orders'}</Typography></TableCell></TableRow>
                ) : rentalOrders.map(order => {
                  const custName = getCustomerName(order);
                  const custPhone = getCustomerPhone(order);
                  return (
                    <TableRow key={order.Id} hover sx={{ cursor: 'pointer' }} onClick={() => { setSelectedOrder(order); setOrderDetailOpen(true); }}>
                      <TableCell><Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>#{order.Id}</Typography></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar src={getImageUrl(order.ProductImage)} sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}><ImageIcon /></Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 80, sm: 120 }, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{order.ProductName || '-'}</Typography>
                            {isMobile && (
                              <Stack direction="row" spacing={0.3} alignItems="center" sx={{ mt: 0.2 }}>
                                {custName && <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem', maxWidth: 70 }}>{custName}</Typography>}
                                {order.IsDelivery && <Chip label="🚚" size="small" color="info" variant="outlined" sx={{ height: 14, fontSize: '0.45rem' }} />}
                              </Stack>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}><PersonIcon sx={{ fontSize: 15, color: 'primary.main' }} /></Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.82rem', maxWidth: 130 }}>
                                {custName || `ID: ${order.CustomerId}`}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="caption" display="block" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{new Date(order.RentalStartDate).toLocaleDateString()}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>→ {new Date(order.RentalEndDate).toLocaleDateString()}</Typography>
                        <Chip label={`${order.RentalDuration} ${isAr ? PRICE_TYPE_LABELS[order.RentalPriceType]?.ar : order.RentalPriceType}`} size="small" sx={{ height: 18, fontSize: '0.6rem', mt: 0.3 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{order.GrandTotal} {order.Currency}</Typography>
                        {order.DepositAmount > 0 && <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>{isAr ? 'تأمين:' : 'Dep:'} {order.DepositAmount}</Typography>}
                        {order.DeliveryFee > 0 && <Typography variant="caption" color="info.main" display="block" sx={{ fontSize: '0.6rem' }}>{isAr ? 'توصيل:' : 'Del:'} {order.DeliveryFee}</Typography>}
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          {order.IsDelivery ? (
                            <Stack spacing={0.3}>
                              <Chip icon={<LocalShippingIcon sx={{ fontSize: 13 }} />} label={isAr ? 'توصيل' : 'Delivery'} size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 22 }} />
                              {order.DeliveryAddress && (
                                <Tooltip title={order.DeliveryAddress} arrow>
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem', maxWidth: 120, display: 'block' }}>{order.DeliveryAddress}</Typography>
                                </Tooltip>
                              )}
                            </Stack>
                          ) : (
                            <Chip icon={<StorefrontIcon sx={{ fontSize: 13 }} />} label={isAr ? 'استلام' : 'Pickup'} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 22, color: 'text.secondary' }} />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip icon={STATUS_CONFIG[order.Status]?.icon} label={isAr ? STATUS_CONFIG[order.Status]?.labelAr : order.Status} size="small"
                          sx={{ bgcolor: alpha(STATUS_CONFIG[order.Status]?.color || '#999', 0.1), color: STATUS_CONFIG[order.Status]?.color, fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.7rem' }, '& .MuiChip-icon': { color: 'inherit' } }} />
                        {order.IsOverdue && <Chip label="!" size="small" color="error" sx={{ ml: 0.5, height: 18, fontSize: '0.55rem' }} />}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={isAr ? 'عرض التفاصيل' : 'View Details'}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setOrderDetailOpen(true); }}>
                            <VisibilityIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={ordersTotalCount} page={ordersPage} onPageChange={(_, p) => setOrdersPage(p)}
            rowsPerPage={ordersPerPage} onRowsPerPageChange={(e) => { setOrdersPerPage(parseInt(e.target.value)); setOrdersPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage={isAr ? 'الصفوف:' : 'Rows:'} />
        </>
      )}
    </Box>
  );

  // ══════════════════════════════════════════
  // RENDER: ORDER DETAIL DIALOG (READ-ONLY)
  // ══════════════════════════════════════════

    // ══════════════════════════════════════════
  // RENDER: ORDER DETAIL DIALOG (READ-ONLY)
  // ══════════════════════════════════════════

  const renderOrderDetail = () => {
    if (!selectedOrder) return null;
    const custName = getCustomerName(selectedOrder);
    const custPhone = getCustomerPhone(selectedOrder);
    const custEmail = getCustomerEmail(selectedOrder);
    const statusCfg = STATUS_CONFIG[selectedOrder.Status] || {};

    return (
      <Portal>
        <Dialog
          open={orderDetailOpen}
          onClose={() => setOrderDetailOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{ zIndex: 99999 }}
          slotProps={{ backdrop: { sx: { zIndex: 99998 } } }}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              m: isMobile ? 0 : 2,
              maxHeight: isMobile ? '100%' : '90vh',
              zIndex: 99999,
            },
          }}
        >
          {/* Header */}
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              {isAr ? 'تفاصيل الطلب' : 'Order Details'}{' '}
              <Chip label={`#${selectedOrder.Id}`} size="small" color="primary" sx={{ fontWeight: 700, ml: 0.5 }} />
            </Typography>
            <IconButton onClick={() => setOrderDetailOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={2.5}>

              {/* Product & Status */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={getImageUrl(selectedOrder.ProductImage)}
                  variant="rounded"
                  sx={{ width: { xs: 56, sm: 64 }, height: { xs: 56, sm: 64 }, borderRadius: 2 }}
                >
                  <ImageIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                    {selectedOrder.ProductName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.3 }}>
                    <Chip
                      icon={statusCfg.icon}
                      label={isAr ? statusCfg.labelAr : (statusCfg.labelEn || selectedOrder.Status)}
                      size="small"
                      sx={{
                        bgcolor: alpha(statusCfg.color || '#999', 0.1),
                        color: statusCfg.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                    {selectedOrder.IsDelivery && (
                      <Chip
                        icon={<LocalShippingIcon sx={{ fontSize: 13 }} />}
                        label={isAr ? 'توصيل' : 'Delivery'}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem' }}
                      />
                    )}
                    {selectedOrder.IsOverdue && (
                      <Chip
                        label={isAr ? 'متأخر' : 'Overdue'}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem' }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>

              {/* Customer Info */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                  {isAr ? 'معلومات العميل' : 'Customer Information'}
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
                        {custName || (isAr ? 'غير معروف' : 'Unknown')}
                      </Typography>
                    </Box>
                  </Stack>
                  {selectedOrder.SelectedColor && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {isAr ? 'اللون' : 'Color'}:
                      </Typography>
                      <Chip label={selectedOrder.SelectedColor} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Rental Period */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.03),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.warning.main, 0.1),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                  {isAr ? 'فترة التأجير' : 'Rental Period'}
                </Typography>
                <Stack spacing={0.8}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {isAr ? 'تاريخ البداية' : 'Start Date'}:
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {new Date(selectedOrder.RentalStartDate).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {isAr ? 'تاريخ النهاية' : 'End Date'}:
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {new Date(selectedOrder.RentalEndDate).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {isAr ? 'المدة' : 'Duration'}:
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {selectedOrder.RentalDuration}{' '}
                      {isAr
                        ? PRICE_TYPE_LABELS[selectedOrder.RentalPriceType]?.ar
                        : selectedOrder.RentalPriceType}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Delivery Section */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: selectedOrder.IsDelivery
                    ? alpha(theme.palette.info.main, 0.04)
                    : alpha(theme.palette.grey[500], 0.03),
                  border: '1px solid',
                  borderColor: selectedOrder.IsDelivery
                    ? alpha(theme.palette.info.main, 0.12)
                    : alpha(theme.palette.divider, 0.08),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                  {isAr ? 'طريقة الاستلام' : 'Delivery Method'}
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {isAr ? 'النوع' : 'Type'}:
                    </Typography>
                    <Chip
                      icon={
                        selectedOrder.IsDelivery
                          ? <LocalShippingIcon sx={{ fontSize: 14 }} />
                          : <StorefrontIcon sx={{ fontSize: 14 }} />
                      }
                      label={
                        selectedOrder.IsDelivery
                          ? (isAr ? 'توصيل' : 'Delivery')
                          : (isAr ? 'من المتجر' : 'In-Shop Pickup')
                      }
                      size="small"
                      color={selectedOrder.IsDelivery ? 'info' : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  </Stack>
                  {selectedOrder.IsDelivery && selectedOrder.DeliveryAddress && (
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', flexShrink: 0 }}>
                        {isAr ? 'العنوان' : 'Address'}:
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem', textAlign: 'right', maxWidth: '60%' }}>
                        {selectedOrder.DeliveryAddress}
                      </Typography>
                    </Stack>
                  )}
                  {selectedOrder.IsDelivery && selectedOrder.Distance > 0 && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {isAr ? 'المسافة' : 'Distance'}:
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                        {Number(selectedOrder.Distance).toFixed(1)} km
                      </Typography>
                    </Stack>
                  )}
                  {selectedOrder.IsDelivery && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {isAr ? 'رسوم التوصيل' : 'Delivery Fee'}:
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="info.main" sx={{ fontSize: '0.85rem' }}>
                        {(selectedOrder.DeliveryFee || 0) > 0
                          ? `${Number(selectedOrder.DeliveryFee).toLocaleString()} ${selectedOrder.Currency}`
                          : (isAr ? 'مجاني' : 'Free')}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Payment Details */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.03),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.success.main, 0.1),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                  {isAr ? 'تفاصيل المبلغ' : 'Payment Details'}
                </Typography>
                <Stack spacing={0.8}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {isAr ? 'سعر الوحدة' : 'Price/Unit'}:
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {selectedOrder.RentalPricePerUnit} {selectedOrder.Currency}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {isAr ? 'إجمالي الإيجار' : 'Rental Total'}:
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {selectedOrder.RentalTotalPrice} {selectedOrder.Currency}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {isAr ? 'التأمين' : 'Deposit'}:
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                      {selectedOrder.DepositAmount || 0} {selectedOrder.Currency}
                    </Typography>
                  </Stack>
                  {selectedOrder.IsDelivery && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {isAr ? 'التوصيل' : 'Delivery'}:
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                        {selectedOrder.DeliveryFee || 0} {selectedOrder.Currency}
                      </Typography>
                    </Stack>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.95rem' }}>
                      {isAr ? 'المبلغ الكلي' : 'Grand Total'}:
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={900} color="primary.main" sx={{ fontSize: '1.1rem' }}>
                      {selectedOrder.GrandTotal} {selectedOrder.Currency}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Return & Status Info */}
              <Stack spacing={0.8}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {isAr ? 'رسوم التأخير' : 'Late Fee'}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                    {selectedOrder.LateFee ? `${selectedOrder.LateFee} ${selectedOrder.Currency}` : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {isAr ? 'إرجاع التأمين' : 'Deposit Returned'}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                    {selectedOrder.DepositReturned ? '✅' : '❌'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {isAr ? 'تاريخ الإرجاع' : 'Actual Return'}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                    {selectedOrder.ActualReturnDate ? new Date(selectedOrder.ActualReturnDate).toLocaleString() : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {isAr ? 'تاريخ الإنشاء' : 'Created'}:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                    {new Date(selectedOrder.CreatedDate).toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>

              {/* Notes */}
              {selectedOrder.CustomerNotes && (
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    {isAr ? 'ملاحظات العميل:' : 'Customer Notes:'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, fontSize: '0.85rem' }}>
                    {selectedOrder.CustomerNotes}
                  </Typography>
                </Box>
              )}
              {selectedOrder.AdminNotes && (
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    {isAr ? 'ملاحظات المدير:' : 'Admin Notes:'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, fontSize: '0.85rem' }}>
                    {selectedOrder.AdminNotes}
                  </Typography>
                </Box>
              )}

            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              variant="outlined"
              onClick={() => setOrderDetailOpen(false)}
              fullWidth={isMobile}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              {isAr ? 'إغلاق' : 'Close'}
            </Button>
          </DialogActions>
        </Dialog>
      </Portal>
    );
  };

  // ══════════════════════════════════════════
  // RENDER: DELETE DIALOG
  // ══════════════════════════════════════════

  const renderDeleteDialog = () => (
    <Portal>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
        sx={{ zIndex: 99999 }}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, m: isMobile ? 0 : 2 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          {isAr ? 'تأكيد الحذف' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography>
            {isAr
              ? `هل أنت متأكد من حذف "${deletingProduct?.ProductName}"؟`
              : `Delete "${deletingProduct?.ProductName}"?`}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {isAr
              ? 'سيتم حذف جميع طلبات التأجير المرتبطة.'
              : 'All related rental orders will also be deleted.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {isAr ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteProduct} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (isAr ? 'حذف' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Portal>
  );

  // ══════════════════════════════════════════
  // MAIN RENDER
  // ══════════════════════════════════════════

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <HomeRepairServiceIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>{isAr ? 'إدارة التأجير' : 'Rental Management'}</Typography>
            <Typography variant="body2" color="text.secondary">{isAr ? 'إدارة منتجات وطلبات التأجير' : 'Manage rental products and orders'}</Typography>
          </Box>
        </Stack>
      </Stack>

      {!branchId && <Alert severity="warning" sx={{ mb: 3 }}>{isAr ? 'يرجى اختيار فرع أولاً' : 'Please select a branch first'}</Alert>}

      {branchId && (
        <>
          {renderStats()}
          {formStep === 1 ? renderCategorySelection() : formStep === 2 ? renderProductForm() : (
            <>
              <Paper sx={{ borderRadius: 2, mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }} variant={isMobile ? "fullWidth" : "standard"}>
                  <Tab icon={<InventoryIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />} label={<Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>{isAr ? 'المنتجات' : 'Products'}</Typography>} iconPosition="start" />
                  <Tab icon={<AssignmentIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />} label={<Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>{isAr ? 'الطلبات' : 'Orders'}</Typography>} iconPosition="start" />
                </Tabs>
              </Paper>
              {tabValue === 0 && renderProductsList()}
              {tabValue === 1 && renderOrdersTab()}
            </>
          )}
        </>
      )}

      {renderOrderDetail()}
      {renderDeleteDialog()}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}